import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import DataSource from 'devextreme/data/data_source'
import Form, { ButtonItem, SimpleItem, EmptyItem, ColCountByScreen, Label, GroupItem } from 'devextreme-react/form'
import 'devextreme-react/tag-box'
import JumboCardQuick from '@jumbo/components/JumboCardQuick'
import Typography from '@mui/material/Typography'
import classesService from 'app/services/pages/courses/setup/classesServices/classesService'
import { useSnackbar } from 'notistack'
import CustomStore from 'devextreme/data/custom_store'
import utilServices from 'app/services/util-services'
import teacherServices from 'app/services/pages/security/teacherServices/teacherServices'
import { IconButton, Stack, useMediaQuery } from '@mui/material'
import WestIcon from '@mui/icons-material/West'

const AddClassSectionForm = (props) => {
    const navigate = useNavigate()
    const formRef = useRef(null)
    const { classId } = useParams()
    const { enqueueSnackbar } = useSnackbar()
    const { termConfig } = useSelector((state) => state.terms)
    const { loading } = useSelector((state) => state.sections)
    const [labelLocation, setLabelLocation] = useState('left')
    const sm = useMediaQuery('(max-width:600px)')

    useEffect(() => {
        setLabelLocation(sm ? 'top' : 'left')
    }, [sm])

    function handleError(error) {
        if (error.status === '401') {
            navigate('/profile/signout')
        } else if (error.detail) {
            enqueueSnackbar(error.detail, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        } else {
            enqueueSnackbar(error, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        }
    }

    const classLookupList = useCallback(
        async (searchTerm) => {
            try {
                return await classesService.getClassLookupList({ termId: termConfig.termId, className: searchTerm })
            } catch (error) {
                handleError(error)
            }
        },
        [termConfig.termId]
    )

    const classLookupById = useCallback(async (key) => {
        try {
            return await classesService.getClassLookupById(key)
        } catch (error) {
            handleError(error)
        }
    }, [])

    const classLookupsStore = useMemo(() => {
        return new DataSource({
            store: new CustomStore({
                key: 'id',
                load: async function (loadOptions) {
                    let searchTerm = ''
                    if (loadOptions.searchValue) {
                        searchTerm = loadOptions.searchValue
                    }
                    return await classLookupList(searchTerm)
                },
                byKey: async function (key) {
                    if (key === '') return utilServices.emptyList()

                    return classLookupById(key)
                },
                errorHandler: function (e) {
                    console.log(e)
                },
            }),
            reshapeOnPush: true,
        })
    }, [])

    const classNameEditorOptions = useMemo(
        () => ({
            dataSource: classLookupsStore,
            readOnly: true,
            displayExpr: 'className',
            valueExpr: 'id',
            value: classId,
            searchEnabled: true,
            showClearButton: true,
        }),
        [classLookupsStore]
    )

    const teacherLookupList = useCallback(async (searchTerm) => {
        try {
            return await teacherServices.getTeacherLookupList({ firstName: searchTerm })
        } catch (error) {
            handleError(error)
        }
    }, [])

    const teacherLookupById = useCallback(async (key) => {
        try {
            return await teacherServices.getTeacherById(key)
        } catch (error) {
            handleError(error)
        }
    }, [])

    const teacherLookupsStore = useMemo(() => {
        return new DataSource({
            store: new CustomStore({
                key: 'id',
                load: async function (loadOptions) {
                    let searchTerm = ''
                    if (loadOptions.searchValue) {
                        searchTerm = loadOptions.searchValue
                    }
                    return await teacherLookupList(searchTerm)
                },
                byKey: async function (key) {
                    if (key === '') return utilServices.emptyList()

                    return teacherLookupById(key)
                },
                errorHandler: function (e) {
                    console.log(e)
                },
            }),
            reshapeOnPush: true,
        })
    }, [])

    const teacherEditorOptions = {
        dataSource: teacherLookupsStore,
        valueExpr: 'id',
        searchEnabled: true,
        showClearButton: true,
        itemTemplate: function (data) {
            return data ? data.firstName.toFullName(data.lastName) : ''
        },
        displayExpr: function (data) {
            return data ? data.firstName.toFullName(data.lastName) : ''
        },
    }

    const submitButtonOptions = {
        text: 'Save',
        type: 'default',
        width: 120,
        useSubmitBehavior: true,
    }

    const cancelButtonOptions = {
        text: 'Cancel',
        type: 'Normal',
        width: 120,
        onClick: () => navigate('/courses/setup/classes', { state: { navigateFrom: 'add-section' } }),
    }

    const validationRules = {
        className: [{ type: 'required', message: 'Class Name is required.' }],
        sectionName: [{ type: 'required', message: 'Section Name is required.' }],
        teacherId: [{ type: 'required', message: 'Teacher Name is required.' }],
    }

    return (
        <React.Fragment>
            <JumboCardQuick
                title={
                    <Stack direction='row' alignItems='center' spacing={1}>
                        <IconButton
                            className='backArrowForm'
                            onClick={() =>
                                navigate(`/courses/setup/classes`, {
                                    state: { navigateFrom: 'add-section' },
                                })
                            }>
                            <WestIcon />
                        </IconButton>
                        <Typography color={'common.white'} variant={'h3'} sx={{ mt: 0.8 }}>
                            Add Section
                        </Typography>
                    </Stack>
                }
                headerSx={{
                    height: 40,
                    bgcolor: 'primary.main',
                }}>
                <form
                    onSubmit={(e) => {
                        e.preventDefault()
                        const formInstance = formRef.current?.instance
                        const data = formInstance
                            ? { ...formInstance.option('formData') }
                            : { ...props.sectionData }
                        const additionalEditor = formInstance?.getEditor?.('additionalTeacherIds')
                        if (additionalEditor) {
                            const val = additionalEditor.option('value')
                            data.additionalTeacherIds = utilServices.sanitizeAdditionalTeacherIds(
                                data,
                                Array.isArray(val) ? val : []
                            )
                        } else {
                            data.additionalTeacherIds = utilServices.sanitizeAdditionalTeacherIds(
                                data,
                                Array.isArray(data.additionalTeacherIds) ? data.additionalTeacherIds : []
                            )
                        }
                        props.addSection(data)
                    }}>
                    <Form
                        ref={formRef}
                        mode='form'
                        formData={props.sectionData}
                        showColonAfterLabel={false}
                        showValidationSummary={false}
                        labelLocation={labelLocation}>
                        <GroupItem caption='New Section' cssClass='form-group-item'>
                            <ColCountByScreen lg={13} md={13} sm={1} xs={1} />
                            <GroupItem />
                            <GroupItem />
                            <GroupItem />
                            <GroupItem cssClass='form-group-item' colSpan={6}>
                                <EmptyItem />
                                <SimpleItem
                                    dataField='classId'
                                    editorType='dxSelectBox'
                                    editorOptions={classNameEditorOptions}
                                    validationRules={validationRules.className}>
                                    <Label text='Class Name' />
                                </SimpleItem>
                                <SimpleItem dataField='sectionName' validationRules={validationRules.sectionName} />
                                <SimpleItem dataField='level' />
                                <SimpleItem
                                    dataField='capacity'
                                    editorType='dxNumberBox'
                                    editorOptions={{ showSpinButtons: 'true' }}
                                />
                                <SimpleItem
                                    dataField='teacherId'
                                    editorType='dxSelectBox'
                                    editorOptions={teacherEditorOptions}
                                    validationRules={validationRules.teacherId}>
                                    <Label text='Primary teacher' />
                                </SimpleItem>
                                <SimpleItem
                                    dataField='additionalTeacherIds'
                                    editorType='dxTagBox'
                                    editorOptions={{
                                        ...teacherEditorOptions,
                                        showSelectionControls: true,
                                        maxDisplayedTags: 3,
                                    }}>
                                    <Label text='Additional teachers' />
                                </SimpleItem>
                                <SimpleItem dataField='description' editorType='dxTextArea' />
                                <SimpleItem dataField='comments' editorType='dxTextArea' />
                            </GroupItem>
                        </GroupItem>
                        <EmptyItem />
                        <GroupItem>
                            <ColCountByScreen lg={2} md={2} sm={2} xs={2} />
                            <ButtonItem
                                buttonOptions={{
                                    disabled: loading,
                                    icon: loading ? 'refresh' : 'check',
                                    ...submitButtonOptions,
                                }}
                                horizontalAlignment='right'
                            />
                            <ButtonItem buttonOptions={cancelButtonOptions} horizontalAlignment='left' />
                        </GroupItem>
                    </Form>
                </form>
            </JumboCardQuick>
        </React.Fragment>
    )
}

export default AddClassSectionForm
